import * as React from 'react'

/**
 * The stacking layer a subtree lives on, for the floating surfaces it opens.
 *
 * Radix portals every dropdown / select / popover / dialog to `document.body`,
 * so their painting order is decided purely by z-index (DOM order only breaks
 * ties). Our shared primitives all default to `z-50`, which is correct on a
 * normal page and correct under a plain flyout.
 *
 * It stops being correct inside a RAISED panel. The app stacks a nested flyout
 * at `z-[60]` and a preview inside it at `z-[70]`. A menu opened from there is
 * still `z-50`, so it paints UNDER the panel that owns the button: the menu is
 * in the DOM, `elementFromPoint` returns the panel, and the click is
 * intercepted. The user sees a button that opens nothing. That is exactly what
 * happened to the asset "..." menu inside `AssetDetailFlyout` — the 2026-08-21
 * e2e found it after the dialog half of the same bug had already been fixed.
 *
 * Rather than make every call site remember to pass a z-index down through
 * components it doesn't own (the "..." menu is three levels below the host that
 * knows the layer), a host declares its layer ONCE and every floating surface
 * beneath it opts in automatically.
 *
 * The value is a Tailwind z class (e.g. `z-[70]`). It is appended after the
 * primitive's own class, and `cn` runs tailwind-merge, so it wins.
 */
const StackingLayerContext = React.createContext<string | undefined>(undefined)

/**
 * 面板（Flyout）画在哪一层的下限（Tailwind z class）。默认没有：面板 z-50、写死的 z-[60] 等照旧。只有显式给了的子树才抬高：
 * AI 助手浮窗（z-[90]）里打开的快速创建给 z-[95]。这个子树里的面板和弹层（StackingLayerProvider）都至少在这一层，
 * 同层时后开的在页面里靠后、画在上面：快速创建 →「已经报过？」→ TaskDetail → 资产 / PO 详情 → 收货弹窗依次在上（2026-10-28，复评 R-1 / R-2）。
 * 不跟随 StackingLayerProvider：顶栏菜单的 z-[96] 只管下拉，不该把顶栏「新建」打开的表单抬上去。
 */
const FlyoutLayerContext = React.createContext<string | undefined>(undefined)
export const FlyoutLayerProvider: React.FC<{ value?: string; children: React.ReactNode }> = ({ value, children }) => (
  <FlyoutLayerContext.Provider value={value}>{children}</FlyoutLayerContext.Provider>
)
/** 这个子树里面板的层下限；没给 = 没有下限 */
export const useFlyoutLayer = (): string | undefined => React.useContext(FlyoutLayerContext)

/** class 里最后一个 z 值（`z-50` / `z-[95]`）；没有 → null */
export function zIndexOf(classes: string | undefined): number | null {
  const found = (classes ?? '').match(/(?:^|\s)z-(?:\[(\d+)\]|(\d+))(?=\s|$)/g)
  if (!found) return null
  const last = found[found.length - 1].trim()
  return Number(last.replace(/^z-\[?/, '').replace(/\]$/, ''))
}

/** `floor` 比 `classes` 里的层高时返回 `floor`（接在后面，tailwind-merge 让它胜出），否则 undefined：只往上抬，不往下压 */
export function liftedTo(classes: string | undefined, floor: string | undefined, fallback = 50): string | undefined {
  const target = zIndexOf(floor)
  if (target == null) return undefined
  return target > (zIndexOf(classes) ?? fallback) ? floor : undefined
}

export const StackingLayerProvider: React.FC<{
  /** Tailwind z class, e.g. `z-[70]`. `undefined` leaves the default in place. */
  value?: string
  children: React.ReactNode
}> = ({ value, children }) => {
  const floor = useFlyoutLayer()
  return <StackingLayerContext.Provider value={liftedTo(value, floor) ?? value}>{children}</StackingLayerContext.Provider>
}

/** The layer class floating surfaces in this subtree should render on. */
export const useStackingLayer = (): string | undefined => React.useContext(StackingLayerContext)

/**
 * 页面上开着、层低于 `below` 的创建面板（CreateFlyout）个数。AI 助手浮窗（z-[90]）在它们开着时沉到面板下面：
 * 用户在收件箱 / 顶栏「新建」里自己打开的表单不被浮窗压住一块（2026-10-29）。不抬面板本身——抬了，从表单叠开的
 * z-50 详情面板（TaskDetail / IntentDetail / AssetDetailFlyout）会被压住（ea1470b75 回退的那次）。
 */
const openPanelLayers = new Map<object, number>()
const panelListeners = new Set<() => void>()
const notifyPanels = () => { for (const l of panelListeners) l() }
const subscribePanels = (l: () => void) => { panelListeners.add(l); return () => { panelListeners.delete(l) } }

/** 创建面板开着期间登记它画在哪一层（z 数值） */
export function useRegisterOpenPanel(open: boolean, z: number): void {
  React.useEffect(() => {
    if (!open) return
    const token = {}
    openPanelLayers.set(token, z)
    notifyPanels()
    return () => { openPanelLayers.delete(token); notifyPanels() }
  }, [open, z])
}

/** 有没有开着的创建面板画在 `below` 这一层之下 */
export function useOpenPanelBelow(below: string): boolean {
  const limit = zIndexOf(below) ?? 0
  return React.useSyncExternalStore(subscribePanels, () => {
    for (const z of openPanelLayers.values()) if (z < limit) return true
    return false
  })
}
